import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { padNumber } from "../../utils/string";

const styles = StyleSheet.create({
  page: {
    padding: 28,
    fontSize: 10,
    fontFamily: "Helvetica",
    backgroundColor: "#ffffff",
  },

  header: {
    textAlign: "center",
    marginBottom: 18,
  },

  title: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#16a34a",
    marginBottom: 4,
  },

  subtitle: {
    fontSize: 10,
    color: "#6b7280",
  },

  section: {
    marginBottom: 12,
    padding: 10,
    border: "1 solid #e5e7eb",
    borderRadius: 6,
  },

  sectionTitle: {
    fontSize: 11,
    fontWeight: "bold",
    marginBottom: 6,
    color: "#111827",
  },

  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 3,
  },

  label: {
    color: "#6b7280",
  },

  value: {
    fontWeight: "bold",
    color: "#111827",
  },

  bookingHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 6,
  },

  bookingId: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#2563eb",
  },

  roomRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 4,
    borderBottom: "1 solid #f3f4f6",
  },

  totalBox: {
    marginTop: 10,
    padding: 10,
    border: "1 solid #111827",
    borderRadius: 6,
  },

  totalText: {
    fontSize: 12,
    fontWeight: "bold",
  },

  qrBox: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
});


export const BookingPdf = ({ data, qr }: any) => {

  if (!data || !data.bookings) return null;

  const bookRefLists = data.bookings.map((bk: any) => padNumber(Number(bk.bookingNumber ?? "")));
  const bookRef = "WCHS-" + bookRefLists.join("-") + ``;

  const total = data.bookings.reduce(
    (acc: number, b: any) =>
      acc +
      b.rooms.reduce(
        (sum: number, r: any) => sum + r.price,
        0
      ),
    0
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>

        {/* HEADER */}
        <View style={styles.header}>
          <Text style={styles.title}>BOOKING CONFIRMED</Text>
          <Text style={styles.subtitle}>
            Please present this document at the hotel reception
          </Text>
        </View>

        {/* BOOKING ID + QR */}
        <View style={styles.section}>
          <View style={styles.qrBox}>
            <View>
              <Text style={styles.label}>Booking ID</Text>
              <Text style={styles.value}>{bookRef}</Text>
            </View>

            {qr && (
              <Image src={qr} style={{ width: 70, height: 70 }} />
            )}
          </View>
        </View>

        {/* GUEST DETAILS */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Guest Details</Text>

          <View style={styles.row}>
            <Text style={styles.label}>Name</Text>
            <Text style={styles.value}>{data.guest.name}</Text>
          </View>

          <View style={styles.row}>
            <Text style={styles.label}>Email</Text>
            <Text style={styles.value}>{data.guest.email}</Text>
          </View>

          <View style={styles.row}>
            <Text style={styles.label}>Phone</Text>
            <Text style={styles.value}>{data.guest.phone}</Text>
          </View>
        </View>

        {/* BOOKING INFO */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Booking Details</Text>
          <View style={styles.row}>
            <Text style={styles.label}>Check-in</Text>
            <Text style={styles.value}>{data.checkIn}</Text>
          </View>

          <View style={styles.row}>
            <Text style={styles.label}>Check-out</Text>
            <Text style={styles.value}>{data.checkOut}</Text>
          </View>

          <View style={styles.row}>
            <Text style={styles.label}>Nights</Text>
            <Text style={styles.value}>{data.nights}</Text>
          </View>
        </View>


        {/* BOOKINGS */}
        <Text style={[styles.sectionTitle, { marginBottom: 6 }]}>
          Room Bookings
        </Text>

        {data.bookings.map((b: any, i: number) => (
          <View key={i} style={styles.section}>
            <View style={styles.bookingHeader}>
              <Text>Booking</Text>
              <Text style={styles.bookingId}>#{padNumber(b.bookingNumber)}</Text>
            </View>

            {b.rooms.map((r: any, j: number) => (
              <View key={j} style={styles.roomRow}>
                <Text>Room {r.number}</Text>
                <Text>₦{r.price.toLocaleString()} / night</Text>
              </View>
            ))}
          </View>
        ))}

        {/* TOTAL */}
        <View style={styles.totalBox}>
          <Text style={styles.totalText}>
            TOTAL: ₦{total.toLocaleString()}
          </Text>
        </View>

      </Page>
    </Document>
  );
};